import { useEffect, useState } from 'react';
import { API_BASE } from '../client/config';
import { Button } from './ui';
import { useWriteGate } from '../nav/useWriteGate';
import type { DeviceGroup } from '../store/deviceGroups';

// =============================================================================
// GroupScopeSetup — 그룹 권한(스코프) 적용 스위치.
//
// 2026-09-14: "기존 콘텐츠 소유·공유 범위 확인" 패널을 없앴다. 소유 그룹이 정해지지 않은 콘텐츠는
// 서버가 적용 시점에 '중앙 소유 · 조직 공유'로 자동 분류한다(deviceGroupRepo.activate). 운영자가
// 확인할 조건은 편성 보존과 기기 그룹 배정 두 가지만 남는다.
// =============================================================================

type Readiness = { enforced: boolean; unclassifiedContent: number; legacyScheduleCount: number; unmappedDevices: number };

export function GroupScopeSetup({ orgId, onApplied }: { orgId?: string; groups?: DeviceGroup[]; onApplied: () => Promise<void> }): JSX.Element {
  const [state, setState] = useState<Readiness | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // 대행 '읽기 전용'에서도 버튼은 자리를 지키고 비활성 + 이유만 붙는다(검수 S1).
  const write = useWriteGate();
  const query = orgId ? `?orgId=${encodeURIComponent(orgId)}` : '';
  useEffect(() => { let alive = true; void fetch(`${API_BASE}/api/device-groups/settings${query}`, { credentials: 'include' })
    .then(async response => { if (!response.ok) throw new Error('그룹 권한 준비 상태를 불러오지 못했습니다.'); return response.json() as Promise<Readiness>; })
    .then(value => { if (alive) { setState(value); setError(null); } }).catch(cause => { if (alive) setError(cause.message); });
    return () => { alive = false; };
  }, [query]);
  async function activate() {
    setPending(true); setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/device-groups/settings${query}`, { method: 'PATCH', credentials: 'include',
        headers: { 'content-type': 'application/json' }, body: JSON.stringify({ enforced: true }) });
      if (!response.ok) { const body = await response.json(); throw new Error(body.error ?? '권한 적용에 실패했습니다.'); }
      setState(await response.json() as Readiness); setConfirming(false); await onApplied();
    } catch (cause) { setError(cause instanceof Error ? cause.message : '권한 적용 오류'); }
    finally { setPending(false); }
  }
  const ready = !!state && !state.legacyScheduleCount && !state.unmappedDevices;
  // 적용 뒤엔 한 줄 상태로 접는다(대교 S4) — 매일 볼 정보가 아니라서.
  if (state?.enforced) return <section className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-panel-2 px-4 py-2 text-sm" aria-label="그룹별 권한 적용 상태">
    <h2 className="text-sm font-semibold">그룹별 권한 적용 중</h2>
    <span className="text-text-dim">— 관리자는 담당 그룹의 기기·콘텐츠만 보고, 그룹의 승인 방식이 서버에서 강제됩니다.</span>
  </section>;
  return <section className="rounded-lg border border-line bg-panel-2 p-4" aria-label="그룹별 권한 적용 상태">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="text-base font-semibold">그룹별 권한 적용 준비</h2>
        <p className="mt-1 text-sm text-text-dim">지금은 모든 관리자가 모든 기기를 봅니다. 기기와 관리자를 그룹에 배정한 뒤 적용하면 담당 그룹만 보이고 승인 방식이 작동합니다. 한 번 적용하면 되돌리지 않습니다. 플레이리스트·라이브러리의 공통/그룹 구분은 적용 전에도 표시됩니다.</p>
      </div>
      <Button variant="primary" {...write.props} disabled={!ready || pending || write.blocked} onClick={() => setConfirming(true)}>그룹별 권한 적용</Button>
    </div>
    {error && <p role="alert" className="mt-3 text-sm text-red">{error}</p>}
    {state && !state.enforced && <>
      <div className="mt-3 flex flex-wrap gap-4 text-sm text-text-dim">
        <span>보존 작업이 필요한 편성 {state.legacyScheduleCount}개</span><span>그룹 확인이 필요한 기기 {state.unmappedDevices}대</span>
      </div>
      {state.legacyScheduleCount > 0 && <p className="mt-2 text-sm text-text-dim">현재 편성을 보존하는 이전 작업이 필요합니다. 기존 편성을 삭제해서 적용 조건을 맞추지 마세요.</p>}
      {confirming && <div role="alertdialog" aria-label="그룹별 권한 적용 확인" className="mt-4 rounded border border-accent-ring bg-accent-soft p-4">
        <p className="text-sm">담당 그룹이 없는 계정은 콘텐츠와 기기에 접근할 수 없게 됩니다. 조직 관리자와 중앙 관리자는 모든 그룹을 계속 관리합니다.
          {state.unclassifiedContent > 0 && ` 소유 그룹이 없는 콘텐츠 ${state.unclassifiedContent}개는 중앙 소유 · 조직 공유로 정리됩니다.`}</p>
        <div className="mt-3 flex gap-2"><Button variant="primary" {...write.props} disabled={pending || write.blocked} onClick={() => void activate()}>{pending ? '적용 중…' : '확인 후 적용'}</Button>
          <Button disabled={pending} onClick={() => setConfirming(false)}>취소</Button></div>
      </div>}
    </>}
  </section>;
}
